import type { PlanVsActual } from '@fieldvision/contracts';
import { Target } from 'lucide-react';

import {
  Badge,
  EmptyState,
  Meter,
  Panel,
  PanelHeader,
  PanelLink,
  Spinner,
} from '../../components/ui/index.js';
import { decimal, integer } from '../../lib/format.js';
import { attainmentTone } from '../../lib/thresholds.js';

/**
 * FR-14 Plan vs Actual — the measures the client named.
 *
 * Each row is a value against its own target, which is why these are meters and
 * not a bar chart: the comparison is within a row, never across them. Visits,
 * a compliance score and two price ratios share no scale and putting them on one
 * axis would be nonsense dressed as a chart.
 *
 * THERE ARE TWO PRICE METERS AGAIN, and they are not the two there used to be.
 * The retired one, "Indeks harga", divided an observed price by OUR list price —
 * are we selling at list? — and read as a band around 100; nothing computes it
 * any more. Both meters here divide one OBSERVED price by another, so LOWER IS
 * BETTER on each, and each badge reports a count rather than the mean's verdict
 * for the reason stated on `CompetitionBadge`.
 *
 * WHAT SEPARATES THEM IS WHICH PLANS THEY COUNT. "Indeks kompetisi" is our price
 * over a RIVAL's. "Indeks internal" is our price over ANOTHER OF OURS — the
 * price ladder migration 0057 introduced. They are deliberately not summed: the
 * first answers how we price against the market, the second how our own range is
 * spaced, and one number blending them would move on a day nobody touched a
 * competitor benchmark at all. Same arithmetic, same grading, two questions.
 *
 * THE MONTH NOTE IS NOT DECORATION. Targets are monthly and the filter is an
 * arbitrary window, so a window covering ten days of January counts 10/31 of
 * January's target. Stating that under the meters is the difference between a
 * number a manager can defend in a review and one they cannot explain.
 *
 * A null target renders "—" and an empty meter, never 0%. Zero reads as total
 * failure; absent reads as what it is.
 */
export function PlanVsActualPanel({
  data,
  isLoading,
  isFetching,
  disabled,
}: {
  data: PlanVsActual | undefined;
  isLoading: boolean;
  isFetching: boolean;
  disabled: boolean;
}) {
  return (
    <Panel className="flex h-full flex-col">
      <div className="flex items-start gap-2.5">
        <Target size={18} className="mt-1 shrink-0 text-steel" aria-hidden />
        <div className="min-w-0 flex-1">
          <PanelHeader
            title="Plan vs Actual"
            description="Target terhadap realisasi pada rentang terpilih."
            action={<PanelLink to="/master/visit-targets" label="Atur target" />}
          />
        </div>
      </div>

      {disabled ? (
        <EmptyState
          title="Rentang tanggal belum valid"
          description="Perbaiki rentang di atas untuk melihat data."
          icon={Target}
        />
      ) : isLoading ? (
        <Spinner label="Memuat plan vs actual…" />
      ) : !data ? (
        <EmptyState title="Belum ada data" icon={Target} />
      ) : (
        <div className={`flex flex-1 flex-col gap-5 transition-opacity duration-150 ${isFetching ? 'opacity-60' : ''}`}>
          <Meter
            // "Outlet dikunjungi", not "Kunjungan": `actual` counts DISTINCT
            // outlets reached, so a shop worked twice moves the check-in total
            // without moving this meter. Labelling it "Kunjungan" would put a
            // number here that no longer matches the one on /reports.
            label="Outlet dikunjungi"
            valueLabel={integer(data.visits.actual)}
            targetLabel={data.visits.target === null ? '—' : integer(Math.round(Number(data.visits.target)))}
            percentage={data.visits.attainmentPct === null ? null : Number(data.visits.attainmentPct)}
            badge={<AttainmentBadge value={data.visits.attainmentPct} />}
            {...(data.visits.salesmenWithTarget > 0
              ? { hint: `${integer(data.visits.salesmenWithTarget)} petugas memiliki target` }
              : { hint: 'Belum ada target kunjungan untuk periode ini.' })}
          />

          {/*
            NO PLANOGRAM METER SINCE SCORING v4, and its absence is the decision.

            `visit_targets.target_planogram_score` was dropped: sales staff are
            accountable for collecting the data, not for the score it produces,
            so there is no plan to put beside an actual. A meter with no target
            is the one thing this panel must not render. The two division scores
            are on the planogram report, where they read against each other.
          */}
          <Meter
            label="Indeks kompetisi"
            valueLabel={decimal(data.competition.actual, 1)}
            targetLabel="100"
            percentage={data.competition.actual === null ? null : Number(data.competition.actual)}
            badge={<CompetitionBadge data={data.competition} />}
            hint={competitionHint(data.competition, 'Belum ada rencana kompetisi.')}
          />

          {/*
           * RENDERED EVEN WITH NO LADDERS, like the visits meter with no targets
           * set. An absent meter and an empty one say different things: the first
           * leaves a manager who was told this exists wondering where it went, the
           * second says "nothing planned yet" in the hint, which is the finding.
           */}
          <Meter
            label="Indeks internal"
            valueLabel={decimal(data.competitionInternal.actual, 1)}
            targetLabel="100"
            percentage={
              data.competitionInternal.actual === null
                ? null
                : Number(data.competitionInternal.actual)
            }
            badge={<CompetitionBadge data={data.competitionInternal} />}
            hint={competitionHint(data.competitionInternal, 'Belum ada tangga harga internal.')}
          />

          {data.monthsCovered.length > 0 ? (
            <p className="mt-auto text-sm text-muted">
              Target bulanan dihitung proporsional:{' '}
              {data.monthsCovered
                .map((month) => `${monthName(month.periodMonth)} ${Math.round(month.weight * 100)}%`)
                .join(' · ')}
              .
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

function AttainmentBadge({ value }: { value: string | null }) {
  const tone = attainmentTone(value);
  if (tone === null) return <Badge tone="neutral">Tanpa target</Badge>;
  return <Badge tone={tone}>{`${Math.round(Number(value))}%`}</Badge>;
}

/**
 * The competition index against the plans' own targets.
 *
 * NOT a band around the target, and that is the point. The retired list-price
 * index measured us against our own list price, where both directions were
 * findings. This one measures OUR price against a RIVAL's, so lower is better —
 * being further under the rival than planned is the best news in the portfolio,
 * and painting it amber is how a dashboard gets ignored.
 *
 * THE BADGE REPORTS THE COUNT, NOT THE MEAN'S VERDICT. `actual` is a mean of a
 * ratio that is not symmetric about 1 — a plan at half its target scores 50, one
 * at double scores 200 — so a badly-under and a badly-over plan average to a
 * number that looks fine. The count cannot cancel; the mean can.
 *
 * The grades come from the server, at the administrable band. This component
 * classifies nothing — see `thresholds.ts` for why that is not an option.
 */
function CompetitionBadge({
  data,
}: {
  // Both meters, unchanged: `competitionInternal` is the same shape and grades on
  // the same rule, so a second badge would only be a second thing to keep in step.
  data: PlanVsActual['competition'] | PlanVsActual['competitionInternal'];
}) {
  if (data.plansWithData === 0) return <Badge tone="neutral">Belum ada data</Badge>;
  if (data.plansNeedAdjustment > 0) {
    return <Badge tone="warn">{`${integer(data.plansNeedAdjustment)} perlu penyesuaian`}</Badge>;
  }
  // Every plan that has data is Baik or better. `Sangat baik` is worth saying
  // out loud rather than folding into "on plan": it is the outcome the whole
  // benchmark exists to produce.
  if (data.plansVeryGood > 0 && data.plansGood === 0) {
    return <Badge tone="success">Semua sangat baik</Badge>;
  }
  return <Badge tone="success">Sesuai rencana</Badge>;
}

/**
 * Leads with coverage when coverage is the story.
 *
 * An index built from 2 of 40 plans is not wrong, but it is not the market
 * either — the same guard `skusWithTarget` gives the price index above.
 *
 * SHARED BY BOTH PRICE METERS, with only the "nothing planned" sentence passed
 * in. Every other line is about coverage and grades, which read identically
 * whether the comparison is against a rival or against one of our own products —
 * and two copies of this function would be two places for the coverage rule to
 * drift.
 */
function competitionHint(data: PlanVsActual['competition'], noPlans: string): string {
  if (data.plans === 0) return noPlans;
  if (data.plansWithData === 0) {
    return `Belum ada observasi berpasangan untuk ${integer(data.plans)} rencana.`;
  }
  const coverage = `${integer(data.plansWithData)} dari ${integer(data.plans)} rencana punya data`;
  if (data.plansWithData * 3 < data.plans) return `${coverage} — cakupan masih tipis.`;
  /*
   * The three-way split rather than one "off plan" count, because after the
   * direction flip they are three different pieces of news. Zero-valued grades
   * are dropped rather than printed as "0 sangat baik", which reads as a finding
   * about nothing.
   */
  const split = [
    data.plansVeryGood > 0 ? `${integer(data.plansVeryGood)} sangat baik` : null,
    data.plansGood > 0 ? `${integer(data.plansGood)} baik` : null,
    data.plansNeedAdjustment > 0 ? `${integer(data.plansNeedAdjustment)} perlu penyesuaian` : null,
  ].filter((part): part is string => part !== null);
  return `${coverage} · ${split.join(', ')} · ${integer(data.pairedOutlets)} pasangan outlet`;
}

function monthName(periodMonth: string): string {
  const date = new Date(`${periodMonth}T00:00:00`);
  if (Number.isNaN(date.getTime())) return periodMonth;
  return date.toLocaleDateString('id-ID', { month: 'long' });
}
